"use client"

import { User } from "@/app/generated/prisma"

import { RAAUTH_ONLY } from "@/lib/auth-stack"
import { Routes } from "@/lib/routes"
import { useSisterSiteNavigation } from "@/utils/navbar/useSisterSiteNavigation"

import { apiService } from "@/lib/api"
import { cn } from "@/utils/helpers/utils"
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react"
import { useQueryClient } from "@tanstack/react-query"
import { signOut } from "next-auth/react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { CiLogout } from "react-icons/ci"
import { FaCaretDown } from "react-icons/fa"
import { GoPerson } from "react-icons/go"
import { IoBriefcaseOutline } from "react-icons/io5"
import Button from "../../Button"

export function UserTile({ user, isMobile }: { user: Partial<User>; isMobile?: boolean }) {
   const router = useRouter()
   const queryClient = useQueryClient()
   const { fetchToken } = useSisterSiteNavigation()

   const handleLogout = async () => {
      // raauth-only mode (dev): hand off to the raauth logout route,
      // which clears the NextAuth session and ends the raauth SSO session.
      if (RAAUTH_ONLY) {
         queryClient.clear()
         window.location.href = "/api/auth/sso-logout"
         return
      }
      await apiService.get(Routes.api.unifiedLogoutApi.getPath(), {})
      queryClient.clear()
      await signOut({ redirect: false })
      router.refresh()
   }

   return (
      <Popover className="relative">
         <PopoverButton className="inline-flex h-full items-center gap-x-1 overflow-hidden rounded-full bg-[#FFF8F0] text-base font-semibold text-gray-900 focus:outline-none active:text-brand-black">
            <div className="mr-auto flex size-10 items-center justify-center gap-0 rounded-full border bg-brand-orange text-xs text-white shadow-xl">{`${user?.firstName?.charAt(0) ?? ""}${user?.lastName?.charAt(0) ?? ""}`}</div>

            {!isMobile && (
               <div className="flex items-center gap-1 pr-2">
                  <p className="mr-auto line-clamp-1 max-w-24 text-ellipsis font-medium">{user?.firstName}</p>
                  <FaCaretDown className="text-brandOrange" />
               </div>
            )}
         </PopoverButton>

         <PopoverPanel
            transition
            className={cn(
               "absolute right-0 top-full z-[100] mt-2 w-max bg-white shadow-xl",
               "transition duration-150 ease-out",
               "data-[closed]:opacity-0",
            )}
         >
            <div
               className="border-top-0 mx-auto w-max max-w-7xl items-center justify-center rounded-lg p-4 shadow-lg"
               style={{
                  boxShadow: "inset 10px 0 0 calc(1px + 0) rgb(17 24 39 / 0.05)",
               }}
            >
               <p className="text-sm font-bold">Membership Benefits</p>
               <Link
                  href={Routes.client.profileEdit.getPath()}
                  className="group relative flex w-full grow items-center justify-start gap-6 rounded-lg px-2 py-2 text-center text-sm leading-6 transition-colors hover:bg-orange-50 sm:flex-row"
               >
                  <GoPerson className="h-6 w-6 text-gray-900 group-hover:text-brandOrange" aria-hidden="true" />
                  <span className="font-medium text-gray-900 group-hover:text-brandOrange">My Profile</span>
               </Link>

               <Button
                  variant={"ghost"}
                  className="group relative flex w-full grow items-center justify-start gap-6 rounded-lg px-2 text-center text-sm leading-6 sm:flex-row"
                  onClick={async () => {
                     const token = await fetchToken("jobs")
                     window.location.href = Routes.external.PostAJob.getPath({ jobs_session: token ?? undefined })
                  }}
               >
                  <IoBriefcaseOutline
                     className="h-6 w-6 text-gray-900 group-hover:text-brandOrange"
                     aria-hidden="true"
                  />
                  <span className="font-medium text-gray-900 group-hover:text-brandOrange">Post a Job</span>
               </Button>

               <Button
                  className="group relative mt-4 flex w-full grow items-center justify-start gap-6 rounded-lg px-2 text-sm leading-6 sm:flex-row"
                  variant={"ghost"}
                  onClick={handleLogout}
               >
                  <div className="absolute inset-0 h-px w-full bg-black/50" />
                  <CiLogout className="h-6 w-6 text-gray-900 group-hover:text-brandOrange" aria-hidden="true" />
                  <span className="font-medium text-gray-900 group-hover:text-brandOrange">Logout</span>
               </Button>
            </div>
         </PopoverPanel>
      </Popover>
   )
}
